import { useEffect } from 'react';
import toast from 'react-hot-toast';
import { WithRouterProps } from 'react-router';

import { TitledPage } from '@client/new/components/Misc/TitledPage';
import { TableLoader } from '@client/new/components/TableShared/TableLoader';
import { useCustomizations } from '@client/new/hooks/use-customizations';
import { PATHS, replacePath } from '@client/routes';

import { ProgramsView } from './Programs.view';
import { pageBackground } from '@client/new/pages/page-background.css';

// Step 1 of the `unified-programs-view` series. This page only:
//   1. Resolves `isUnifiedProgramsEnabled` via `useCustomizations`.
//   2. While loading -> renders an empty titled page with a table skeleton.
//   3. Flag not explicitly TRUE (false, null, or customizations error) ->
//      replaces history with the legacy `/otps` route. Day-one creators
//      whose backend default is `null` should not be stranded on a loader.
//   4. Flag ON -> renders the `ProgramsView`.
//
// Design notes:
// - Redirect runs in `useEffect`, not in the render body, so React 18
//   strict-mode double-render does not fire it twice.
// - Uses `replacePath` (not `navigate` which calls `push`) so the back
//   button does not bounce the user through `/programs`.
// - On Apollo error, we toast once and redirect so the user gets a signal
//   rather than a silent bounce.
// - Guards `Number(influencerIdParam)` against `NaN` so `/programs/foo`
//   does not redirect to `/otps/NaN`.
export const ProgramsPage = (p: WithRouterProps) => {
  const influencerIdParam = p.params?.influencerId;
  const { isLoading, error, customizations } = useCustomizations(
    influencerIdParam ?? null,
  );

  const flag = customizations?.isUnifiedProgramsEnabled;
  const hasError = !!error;
  const shouldRedirect = !isLoading && (flag !== true || hasError);

  const parsedInfluencerId = influencerIdParam
    ? Number(influencerIdParam)
    : NaN;
  const redirectTarget = Number.isFinite(parsedInfluencerId)
    ? PATHS.PURCHASES(parsedInfluencerId)
    : '/otps';

  useEffect(() => {
    if (!shouldRedirect) return;
    if (hasError) {
      toast.error('Unable to load programs. Redirecting…');
    }
    replacePath(redirectTarget);
  }, [shouldRedirect, hasError, redirectTarget]);

  if (isLoading || flag !== true) {
    return (
      <div className={pageBackground}>
        <TitledPage title="Programs">
          <TableLoader />
        </TitledPage>
      </div>
    );
  }

  return (
    <div className={pageBackground}>
      <ProgramsView influencerId={influencerIdParam} />
    </div>
  );
};
